import { IBannerComponent } from "@faire/web-api/indigofair/data/IBannerComponent";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import React from "react";

import {
  objectFitCoverImageStyles,
  SDUIImage,
} from "@faire/retailer/components/SDUI/__internal__/Image/SDUIImage";
import {
  convertServerResponsivePropertyToClientResponsiveProperty,
  getResponsiveValueAtIndex,
} from "@faire/retailer/components/SDUI/__internal__/Styling/responsiveValues";
import {
  ComponentLoggingEventParams,
  SDUIComponentRenderBehaviour,
} from "@faire/retailer/components/SDUI/__internal__/types";

import { SDUIButtonRenderer } from "../../../Button/SDUIButtonRenderer";
import { SDUITypographyRenderer } from "../../../Typography/SDUITypographyRenderer";

import {
  buttonWidthContainer,
  centerAlignedWrapper,
  imageStyle,
} from "./__internal__/style.css";

type VerticalTextImageBannerProps = {
  bannerComponent: IBannerComponent;
  loggingData: ComponentLoggingEventParams;
  renderBehaviour?: SDUIComponentRenderBehaviour;
};

const OPTIMIZE_IMAGE_OPTIONS = {
  mobileAndAbove: {
    width: 430,
  },
  tabletAndAbove: {
    height: 200,
  },
};

export const SDUIVerticalModalTextImageBannerRenderer: React.FC<
  VerticalTextImageBannerProps
> = ({ bannerComponent, loggingData, renderBehaviour }) => {
  const {
    images,
    responsive_images,
    title,
    tagline,
    description,
    button,
    disclaimer,
  } = bannerComponent;

  const responsiveImages =
    convertServerResponsivePropertyToClientResponsiveProperty(
      images,
      (img) => img,
      responsive_images
    );

  return (
    <Column>
      <SDUIImage
        responsiveImage={getResponsiveValueAtIndex(
          responsiveImages,
          0,
          IImage.build()
        )}
        optimizeImageOptions={OPTIMIZE_IMAGE_OPTIONS}
        renderBehaviour={renderBehaviour}
        className={imageStyle}
        imageStyles={objectFitCoverImageStyles}
      />
      <Column
        style={{
          padding: getSpacing("5x", "4x"),
          zIndex: 1,
        }}
      >
        {tagline ? (
          <>
            <SDUITypographyRenderer
              textElement={tagline}
              loggingData={loggingData}
            />
            <Spacer height="3x" />
          </>
        ) : null}
        {title ? (
          <>
            <SDUITypographyRenderer
              textElement={title}
              loggingData={loggingData}
            />
            <Spacer
              height={{
                mobileAndAbove: "3x",
                tabletAndAbove: "2x",
              }}
            />
          </>
        ) : null}
        {description.length > 0 ? (
          <>
            {description.map((descriptionElement, index) => (
              <React.Fragment key={index}>
                {index > 0 ? <Spacer height="1/2x" /> : null}
                <SDUITypographyRenderer
                  textElement={descriptionElement}
                  loggingData={loggingData}
                />
              </React.Fragment>
            ))}
            <Spacer height="3x" />
          </>
        ) : null}
        {button ? (
          <div className={centerAlignedWrapper}>
            <SDUIButtonRenderer
              buttonElement={button}
              className={buttonWidthContainer}
              loggingData={loggingData}
              fireLegacyOnClickEvent
            />
            <Spacer
              height={{
                mobileAndAbove: "3x",
                tabletAndAbove: "2x",
              }}
            />
          </div>
        ) : null}
        {disclaimer ? (
          <>
            <SDUITypographyRenderer
              textElement={disclaimer}
              loggingData={loggingData}
            />
          </>
        ) : null}
      </Column>
    </Column>
  );
};
